import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    Typography,
    Box,
    Stack,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    SelectChangeEvent,
    Alert,
    Snackbar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation, gql } from '@apollo/client';

const UPDATE_DEALER_TICKET = gql`
  mutation UpdateDealerTicket(
    $updateDealerTicketId: Int!
    $status: String!
    $comment: String!
  ) {
    updateDealerTicket(
      id: $updateDealerTicketId
      status: $status
      comment: $comment
    ) {
      code
      message
    }
  }
`;

interface UpdateTicketDialogProps {
    open: boolean;
    onClose: () => void;
    ticketData?: any;
    onSubmit?: (data: any) => void;
}

const UpdateTicketDialog: React.FC<UpdateTicketDialogProps> = ({
    open,
    onClose,
    ticketData,
    onSubmit
}) => {
    const [ticketStatus, setTicketStatus] = useState('');
    const [comment, setComment] = useState('');
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);

    const [originalValues, setOriginalValues] = useState({ status: '', comment: '' });

    const [updateTicket, { loading }] = useMutation(UPDATE_DEALER_TICKET);

    useEffect(() => {
        if (open && ticketData) {
            const initialStatus = ticketData.status || '';
            const initialComment = ticketData.comment || '';
            setTicketStatus(initialStatus);
            setComment(initialComment);
            setSubmitError(null);
            setSuccessSnackbarOpen(false); 
            setOriginalValues({ status: initialStatus, comment: initialComment });
        }
    }, [open, ticketData]);

    const handleStatusChange = (event: SelectChangeEvent<string>) => {
        setTicketStatus(event.target.value);
    };

    const hasChanges = ticketStatus !== originalValues.status || comment !== originalValues.comment;

    const handleSubmit = async () => {
        if (!ticketData?.id) {
            setSubmitError('Ticket ID is missing');
            return;
        }

        if (!ticketStatus) {
            setSubmitError('Please select a status');
            return;
        }

        setSubmitError(null);

        try {
            const { data } = await updateTicket({
                variables: {
                    updateDealerTicketId: Number(ticketData.id),
                    status: ticketStatus,
                    comment: comment.trim()
                }
            });

            const result = data?.updateDealerTicket;

            if (result?.code === 'SUCCESS' || result?.code === 200 || result?.code === '200') {
                if (onSubmit) {
                    onSubmit({
                        ...ticketData,
                        status: ticketStatus,
                        comment: comment.trim()
                    });
                }
                setSuccessSnackbarOpen(true);
                onClose();
            } else {
                setSubmitError(result?.message || 'Failed to update ticket');
            }
        } catch (err: any) {
            console.error('Update error:', err);
            setSubmitError(err.message || 'An error occurred while updating the ticket');
        }
    };

    const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setSuccessSnackbarOpen(false);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 2,
                        width: '100%',
                        maxWidth: '600px'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '24px 24px 16px 24px'
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#333',mb:2 }}>
                        Update Ticket
                    </Typography>
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            mb:2,
                            color: '#000',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ padding: '0px 24px 24px 24px' }}>
                    <Stack spacing={3} sx={{ pt: 1 }}>
                        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
                            <Box sx={{ flex: 1 }}>
                                <TextField
                                    fullWidth
                                    label="Ticket Number"
                                    value={ticketData?.ticketNumber || ''}
                                    disabled
                                    variant="outlined"
                                    size="medium"
                                    InputProps={{
                                        style: { color: '#666' }
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel id="ticket-status-label" shrink>
                                        Ticket Status*
                                    </InputLabel>
                                    <Select
                                        labelId="ticket-status-label"
                                        value={ticketStatus}
                                        onChange={handleStatusChange}
                                        label="Ticket Status*"
                                        displayEmpty
                                    >
                                        <MenuItem value="RAISED">RAISED</MenuItem>
                                        <MenuItem value="IN_PROGRESS">IN_PROGRESS</MenuItem>
                                        <MenuItem value="RESOLVED">RESOLVED</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>

                        <TextField
                            fullWidth
                            label="Comment"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder="Enter your comment here..."
                            variant="outlined"
                            size="medium"
                            multiline
                            rows={3}
                            InputLabelProps={{ shrink: true }}
                        />

                        {submitError && (
                            <Alert severity="error" sx={{ mt: 1 }}>
                                {submitError}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={onClose} variant="outlined" disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="error"
                        disabled={loading || !ticketStatus || !hasChanges}
                    >
                        {loading ? 'Updating...' : 'Submit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/*SUCCESS SNACKBAR */}
            <Snackbar
                open={successSnackbarOpen}
                autoHideDuration={1000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="success"
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    Ticket updated successfully!
                </Alert>
            </Snackbar>
        </>
    );
};

export default UpdateTicketDialog;